/**
 * GENERADOR DE CÓDIGOS QR (FASE 8.1) — sin dependencias
 *
 * El QR de un activo sólo lleva la URL de su ficha (`/activos/<uuid>`), así
 * que basta con un subconjunto pequeño de la norma (ISO/IEC 18004):
 *
 *   · modo byte (UTF-8), nivel de corrección M (~15 %);
 *   · versiones 1 a 10 (hasta ~213 bytes: sobra para cualquier URL de ficha);
 *   · las 8 máscaras, eligiendo la de menor penalización (reglas N1–N4).
 *
 * Se genera en el navegador cada vez: la imagen nunca se guarda en la base.
 * Algoritmo según la norma y la implementación de referencia de Project
 * Nayuki (MIT); validado bit a bit contra otra implementación (`segno`) en
 * las 8 máscaras — ver el informe de la fase.
 */

/** [total de codewords, EC por bloque, bloques del grupo 1, datos g1, bloques g2, datos g2] — nivel M. */
const TABLA_M: readonly (readonly [number, number, number, number, number, number])[] = [
  [26, 10, 1, 16, 0, 0],
  [44, 16, 1, 28, 0, 0],
  [70, 26, 1, 44, 0, 0],
  [100, 18, 2, 32, 0, 0],
  [134, 24, 2, 43, 0, 0],
  [172, 16, 4, 27, 0, 0],
  [196, 18, 4, 31, 0, 0],
  [242, 22, 2, 38, 2, 39],
  [292, 22, 3, 36, 2, 37],
  [346, 26, 4, 43, 1, 44],
];

const ALINEACION: readonly (readonly number[])[] = [
  [], [6, 18], [6, 22], [6, 26], [6, 30], [6, 34], [6, 22, 38], [6, 24, 42], [6, 26, 46], [6, 28, 50],
];

const VERSION_MAXIMA = 10;

// ---------------------------------------------------------------- Reed-Solomon GF(256)

const EXP = new Uint8Array(512);
const LOG = new Uint8Array(256);
(() => {
  let x = 1;
  for (let i = 0; i < 255; i++) {
    EXP[i] = x;
    LOG[x] = i;
    x <<= 1;
    if (x & 0x100) x ^= 0x11d;
  }
  for (let i = 255; i < 512; i++) EXP[i] = EXP[i - 255]!;
})();

function multiplicar(a: number, b: number): number {
  return a === 0 || b === 0 ? 0 : EXP[LOG[a]! + LOG[b]!]!;
}

function generador(grado: number): number[] {
  //  Coeficientes de mayor a menor grado, sin el término líder (siempre 1).
  const r = new Array<number>(grado).fill(0);
  r[grado - 1] = 1;
  let raiz = 1;
  for (let i = 0; i < grado; i++) {
    for (let j = 0; j < grado; j++) {
      r[j] = multiplicar(r[j]!, raiz);
      if (j + 1 < grado) r[j] = r[j]! ^ r[j + 1]!;
    }
    raiz = multiplicar(raiz, 0x02);
  }
  return r;
}

function restoRs(datos: readonly number[], gen: readonly number[]): number[] {
  const r = new Array<number>(gen.length).fill(0);
  for (const b of datos) {
    const f = b ^ r.shift()!;
    r.push(0);
    for (let i = 0; i < gen.length; i++) r[i] = r[i]! ^ multiplicar(gen[i]!, f);
  }
  return r;
}

// ---------------------------------------------------------------- codificación

function capacidadDatos(version: number): number {
  const [, , b1, d1, b2, d2] = TABLA_M[version - 1]!;
  return b1 * d1 + b2 * d2;
}

function codewordsDeDatos(bytes: Uint8Array, version: number): number[] {
  const bits: number[] = [];
  const poner = (valor: number, n: number): void => {
    for (let i = n - 1; i >= 0; i--) bits.push((valor >>> i) & 1);
  };
  poner(0b0100, 4);
  poner(bytes.length, version < 10 ? 8 : 16);
  for (const b of bytes) poner(b, 8);
  const capacidadBits = capacidadDatos(version) * 8;
  poner(0, Math.min(4, capacidadBits - bits.length));
  poner(0, (8 - (bits.length % 8)) % 8);
  const out: number[] = [];
  for (let i = 0; i < bits.length; i += 8) {
    let v = 0;
    for (let j = 0; j < 8; j++) v = (v << 1) | bits[i + j]!;
    out.push(v);
  }
  for (let relleno = 0xec; out.length < capacidadDatos(version); relleno ^= 0xec ^ 0x11) out.push(relleno);
  return out;
}

function intercalar(datos: readonly number[], version: number): number[] {
  const [, ec, b1, d1, b2, d2] = TABLA_M[version - 1]!;
  const gen = generador(ec);
  const bloques: number[][] = [];
  const ecs: number[][] = [];
  let k = 0;
  for (let i = 0; i < b1 + b2; i++) {
    const n = i < b1 ? d1 : d2;
    const bloque = datos.slice(k, k + n);
    k += n;
    bloques.push(bloque);
    ecs.push(restoRs(bloque, gen));
  }
  const out: number[] = [];
  const maxDatos = Math.max(d1, d2);
  for (let i = 0; i < maxDatos; i++) for (const b of bloques) if (i < b.length) out.push(b[i]!);
  for (let i = 0; i < ec; i++) for (const e of ecs) out.push(e[i]!);
  return out;
}

// ---------------------------------------------------------------- matriz

interface Matriz {
  readonly n: number;
  readonly m: boolean[][];
  readonly funcion: boolean[][];
}

function nuevaMatriz(version: number): Matriz {
  const n = version * 4 + 17;
  return {
    n,
    m: Array.from({ length: n }, () => new Array<boolean>(n).fill(false)),
    funcion: Array.from({ length: n }, () => new Array<boolean>(n).fill(false)),
  };
}

/** (x = columna, y = fila), como en la referencia. */
function fijar(q: Matriz, x: number, y: number, oscuro: boolean): void {
  q.m[y]![x] = oscuro;
  q.funcion[y]![x] = true;
}

function patronesDeFuncion(q: Matriz, version: number): void {
  const { n } = q;
  for (let i = 0; i < n; i++) {
    fijar(q, 6, i, i % 2 === 0);
    fijar(q, i, 6, i % 2 === 0);
  }
  const buscador = (cx: number, cy: number): void => {
    for (let dy = -4; dy <= 4; dy++) {
      for (let dx = -4; dx <= 4; dx++) {
        const d = Math.max(Math.abs(dx), Math.abs(dy));
        const x = cx + dx;
        const y = cy + dy;
        if (x >= 0 && x < n && y >= 0 && y < n) fijar(q, x, y, d !== 2 && d !== 4);
      }
    }
  };
  buscador(3, 3);
  buscador(n - 4, 3);
  buscador(3, n - 4);
  const pos = ALINEACION[version - 1]!;
  for (let i = 0; i < pos.length; i++) {
    for (let j = 0; j < pos.length; j++) {
      if ((i === 0 && j === 0) || (i === 0 && j === pos.length - 1) || (i === pos.length - 1 && j === 0)) continue;
      for (let dy = -2; dy <= 2; dy++) {
        for (let dx = -2; dx <= 2; dx++) {
          fijar(q, pos[i]! + dx, pos[j]! + dy, Math.max(Math.abs(dx), Math.abs(dy)) !== 1);
        }
      }
    }
  }
  bitsDeFormato(q, 0); // reserva; se reescribe con la máscara real
  if (version >= 7) {
    let rem = version;
    for (let i = 0; i < 12; i++) rem = (rem << 1) ^ ((rem >>> 11) * 0x1f25);
    const bits = (version << 12) | rem;
    for (let i = 0; i < 18; i++) {
      const bit = ((bits >>> i) & 1) === 1;
      const a = n - 11 + (i % 3);
      const b = Math.floor(i / 3);
      fijar(q, a, b, bit);
      fijar(q, b, a, bit);
    }
  }
}

function bitsDeFormato(q: Matriz, mascara: number): void {
  const { n } = q;
  const datos = (0 << 3) | mascara; // nivel M = 00
  let rem = datos;
  for (let i = 0; i < 10; i++) rem = (rem << 1) ^ ((rem >>> 9) * 0x537);
  const bits = ((datos << 10) | rem) ^ 0x5412;
  const bit = (i: number): boolean => ((bits >>> i) & 1) === 1;
  for (let i = 0; i <= 5; i++) fijar(q, 8, i, bit(i));
  fijar(q, 8, 7, bit(6));
  fijar(q, 8, 8, bit(7));
  fijar(q, 7, 8, bit(8));
  for (let i = 9; i < 15; i++) fijar(q, 14 - i, 8, bit(i));
  for (let i = 0; i < 8; i++) fijar(q, n - 1 - i, 8, bit(i));
  for (let i = 8; i < 15; i++) fijar(q, 8, n - 15 + i, bit(i));
  fijar(q, 8, n - 8, true);
}

function colocarDatos(q: Matriz, cw: readonly number[]): void {
  const { n } = q;
  let i = 0;
  for (let derecha = n - 1; derecha >= 1; derecha -= 2) {
    if (derecha === 6) derecha = 5;
    for (let v = 0; v < n; v++) {
      for (let j = 0; j < 2; j++) {
        const x = derecha - j;
        const subiendo = ((derecha + 1) & 2) === 0;
        const y = subiendo ? n - 1 - v : v;
        if (!q.funcion[y]![x] && i < cw.length * 8) {
          q.m[y]![x] = ((cw[i >>> 3]! >>> (7 - (i & 7))) & 1) === 1;
          i++;
        }
      }
    }
  }
}

const MASCARAS: readonly ((x: number, y: number) => boolean)[] = [
  (x, y) => (x + y) % 2 === 0,
  (_x, y) => y % 2 === 0,
  (x) => x % 3 === 0,
  (x, y) => (x + y) % 3 === 0,
  (x, y) => (Math.floor(x / 3) + Math.floor(y / 2)) % 2 === 0,
  (x, y) => ((x * y) % 2) + ((x * y) % 3) === 0,
  (x, y) => (((x * y) % 2) + ((x * y) % 3)) % 2 === 0,
  (x, y) => (((x + y) % 2) + ((x * y) % 3)) % 2 === 0,
];

function aplicarMascara(q: Matriz, k: number): void {
  const f = MASCARAS[k]!;
  for (let y = 0; y < q.n; y++) for (let x = 0; x < q.n; x++) if (!q.funcion[y]![x] && f(x, y)) q.m[y]![x] = !q.m[y]![x];
}

/** Penalización N1–N4 (misma formulación que la referencia). */
function penalizacion(q: Matriz): number {
  const { n, m } = q;
  let p = 0;
  const linea = (get: (i: number) => boolean): void => {
    let color = false;
    let run = 0;
    const hist = [0, 0, 0, 0, 0, 0, 0];
    const empujar = (r: number): void => {
      if (hist[0] === 0) r += n;
      hist.pop();
      hist.unshift(r);
    };
    const cuentaBuscador = (): number => {
      const k = hist[1]!;
      const core = k > 0 && hist[2] === k && hist[3] === k * 3 && hist[4] === k && hist[5] === k;
      return (core && hist[0]! >= k * 4 && hist[6]! >= k ? 1 : 0) + (core && hist[6]! >= k * 4 && hist[0]! >= k ? 1 : 0);
    };
    for (let i = 0; i < n; i++) {
      if (get(i) === color) {
        run++;
        if (run === 5) p += 3;
        else if (run > 5) p++;
      } else {
        empujar(run);
        if (!color) p += cuentaBuscador() * 40;
        color = get(i);
        run = 1;
      }
    }
    //  Cierre: el borde cuenta como claro.
    if (color) {
      empujar(run);
      run = 0;
    }
    run += n;
    empujar(run);
    p += cuentaBuscador() * 40;
  };
  for (let y = 0; y < n; y++) linea((x) => m[y]![x]!);
  for (let x = 0; x < n; x++) linea((y) => m[y]![x]!);
  for (let y = 0; y < n - 1; y++) {
    for (let x = 0; x < n - 1; x++) {
      const c = m[y]![x];
      if (c === m[y]![x + 1] && c === m[y + 1]![x] && c === m[y + 1]![x + 1]) p += 3;
    }
  }
  let oscuros = 0;
  for (const fila of m) for (const c of fila) if (c) oscuros++;
  const total = n * n;
  p += (Math.ceil(Math.abs(oscuros * 20 - total * 10) / total) - 1) * 10;
  return p;
}

export interface CodigoQr {
  readonly version: number;
  readonly mascara: number;
  /** `modulos[fila][columna]`, true = oscuro. Sin zona de silencio. */
  readonly modulos: readonly (readonly boolean[])[];
}

/**
 * Codifica `texto` (UTF-8). `mascara` fuerza una máscara (sólo para
 * pruebas); por defecto se elige la de menor penalización.
 */
export function generarQr(texto: string, mascara?: number): CodigoQr {
  const bytes = new TextEncoder().encode(texto);
  let version = 1;
  while (version <= VERSION_MAXIMA && 4 + (version < 10 ? 8 : 16) + bytes.length * 8 > capacidadDatos(version) * 8) version++;
  if (version > VERSION_MAXIMA) throw new RangeError('Texto demasiado largo para un QR de etiqueta.');
  const cw = intercalar(codewordsDeDatos(bytes, version), version);
  const q = nuevaMatriz(version);
  patronesDeFuncion(q, version);
  colocarDatos(q, cw);
  let elegida = mascara ?? -1;
  if (elegida < 0) {
    let mejor = Infinity;
    for (let k = 0; k < 8; k++) {
      aplicarMascara(q, k);
      bitsDeFormato(q, k);
      const pk = penalizacion(q);
      if (pk < mejor) {
        mejor = pk;
        elegida = k;
      }
      aplicarMascara(q, k); // deshace (XOR)
    }
  }
  aplicarMascara(q, elegida);
  bitsDeFormato(q, elegida);
  return { version, mascara: elegida, modulos: q.m };
}

/** Trazado SVG (`path`) de los módulos oscuros, con zona de silencio de 4 módulos. */
export function trazadoQr(qr: CodigoQr): { lado: number; d: string } {
  const borde = 4;
  const partes: string[] = [];
  qr.modulos.forEach((fila, y) =>
    fila.forEach((oscuro, x) => {
      if (oscuro) partes.push(`M${String(x + borde)},${String(y + borde)}h1v1h-1z`);
    }),
  );
  return { lado: qr.modulos.length + borde * 2, d: partes.join('') };
}

/**
 * URL que codifica el QR de un activo: la MISMA identidad para todos
 * (FASE 8.1). Al abrirla, la app resuelve por rol: ficha interna para el
 * personal, ficha pública del área cliente para el CLIENTE (FASE 8.4/9.1-B).
 */
export function urlFicha(id: string): string {
  return `${window.location.origin}/activos/${id}`;
}
